import React, { useEffect, useMemo, useRef, useState } from 'react';
import { router } from '@inertiajs/react';
import SignupLayout from '../Layout.jsx';
import SeatStepper from '../../../components/SeatStepper.jsx';
import ContactSalesModal from '../../../components/ContactSalesModal.jsx';
import { formatPrice } from '../../../constants/reviewVariants.js';
import { useDraftAutosave } from '../../../hooks/useDraftAutosave.js';
import { primaryBtnClass, secondaryBtnClass, ITEM_DEFS, VOLUME_TIERS, VOLUME_DISCOUNT_TIERS, PersonIcon, ShieldIcon, iconFor, saveDraftPatch, prepareCheckout } from '../shared.jsx';
import { getStripePromise } from '../../../components/PaymentForm.jsx';

const DEFAULT_SEATS = ITEM_DEFS.reduce((acc, d) => ({ ...acc, [d.key]: 0 }), {});

export default function GroupTraining({ facility_type, seats: initialSeats, plan: initialPlan, buyer, facility, publishable_key, pricing, progress }) {
  const [seats, setSeats] = useState({ ...DEFAULT_SEATS, ...(initialSeats || {}) });
  const [plan, setPlan] = useState(initialPlan || 'subscription');
  const [submitting, setSubmitting] = useState(false);
  const [contactSalesOpen, setContactSalesOpen] = useState(false);
  const prepareTimer = useRef(null);

  useDraftAutosave('group.seats', seats);
  useDraftAutosave('group.plan', plan);

  // Kick off Stripe.js load early so the script is cached by the time the checkout page mounts.
  useEffect(() => { getStripePromise(publishable_key); }, [publishable_key]);

  // Background-pre-create the Stripe checkout session (debounced) so navigation to /checkout is near-instant.
  useEffect(() => {
    if (prepareTimer.current) clearTimeout(prepareTimer.current);
    const totalSeats = Object.values(seats).reduce((a, b) => a + b, 0);
    if (totalSeats <= 0) return;
    prepareTimer.current = setTimeout(async () => {
      await saveDraftPatch({ group: { seats, plan } });
      prepareCheckout('group');
    }, 800);
    return () => { if (prepareTimer.current) clearTimeout(prepareTimer.current); };
  }, [seats, plan]);

  const totalSeats = useMemo(() => Object.values(seats).reduce((a, b) => a + b, 0), [seats]);
  const currentTier = totalSeats >= 20 ? '20+' : totalSeats >= 10 ? '10-19' : totalSeats >= 5 ? '5-9' : null;
  const showContactOption = totalSeats >= 20;

  const availableItems = useMemo(
    () => ITEM_DEFS.filter((d) => !d.hidden && (pricing?.[d.key]?.subscription || pricing?.[d.key]?.one_time)),
    [pricing]
  );
  const caregiverItems = availableItems.filter((d) => d.group === 'caregiver');
  const adminItems = availableItems.filter((d) => d.group === 'admin');

  const computeLineSubtotal = (item) => {
    const price = pricing[item.key];
    const planKey = plan === 'subscription' ? 'subscription' : 'one_time';
    const cents = price?.[planKey]?.unit_cents;
    if (cents == null) return null;
    return cents * seats[item.key];
  };

  const selectedItems = useMemo(() => availableItems.filter((d) => seats[d.key] > 0), [seats, availableItems]);

  const planAvailability = useMemo(() => {
    if (selectedItems.length === 0) return { subscription: false, one_time: false };
    return {
      subscription: selectedItems.every((it) => pricing?.[it.key]?.subscription),
      one_time:     selectedItems.every((it) => pricing?.[it.key]?.one_time),
    };
  }, [selectedItems, pricing]);

  useEffect(() => {
    if (selectedItems.length === 0) return;
    if (plan === 'subscription' && !planAvailability.subscription && planAvailability.one_time) setPlan('one_time');
    else if (plan === 'one_time' && !planAvailability.one_time && planAvailability.subscription) setPlan('subscription');
  }, [plan, planAvailability, selectedItems.length]);

  const orderSubtotal = useMemo(() => selectedItems.reduce((sum, item) => sum + (computeLineSubtotal(item) || 0), 0), [selectedItems, seats, plan, pricing]);
  const orderDiscount = useMemo(() => {
    const tier = VOLUME_DISCOUNT_TIERS.find((t) => totalSeats >= t.min);
    if (!tier) return 0;
    return Math.round((orderSubtotal * tier.pct) / 100);
  }, [orderSubtotal, totalSeats]);
  const orderTotal = Math.max(0, orderSubtotal - orderDiscount);

  const canAdvance = totalSeats > 0 && (planAvailability.subscription || planAvailability.one_time);

  const handleContinue = async () => {
    if (!canAdvance || submitting) return;
    setSubmitting(true);
    await saveDraftPatch({ group: { seats, plan } });
    router.visit('/signup/group/checkout');
  };

  return (
    <SignupLayout progress={progress}>
      <div className="space-y-5">
        <div>
          <h2 className="text-2xl/9 font-bold tracking-tight text-gray-900">Build your team training plan</h2>
          <p className="text-sm text-gray-600">Select the training types and number of seats your team needs. You can assign users after purchase.</p>
        </div>

        <div className="rounded-md bg-indigo-50 border border-indigo-100 p-3 flex items-start gap-3">
          <ShieldIcon className="h-5 w-5 text-indigo-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs font-medium text-indigo-900">Volume discounts apply automatically based on total seats selected.</p>
            <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
              {VOLUME_TIERS.map((tier) => (
                <div key={tier.label}>
                  <p className="font-medium text-gray-700">{tier.range}</p>
                  {tier.label === '20+' ? (
                    <button type="button" onClick={() => setContactSalesOpen(true)} className="font-semibold text-indigo-600 hover:text-indigo-700 underline underline-offset-2 cursor-pointer">Contact us</button>
                  ) : (
                    <p className={currentTier === tier.label ? 'font-bold text-green-700' : 'font-semibold text-gray-500'}>{tier.pct}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {caregiverItems.length > 0 && (
          <div>
            <h3 className="flex items-center gap-2 font-semibold text-gray-900 mb-3"><PersonIcon /> Caregiver / Direct Care Staff</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {caregiverItems.map((d) => (
                <SeatStepper key={d.key} icon={iconFor[d.key]} label={d.label} description={d.hours}
                  value={seats[d.key]} onChange={(v) => setSeats({ ...seats, [d.key]: v })} />
              ))}
            </div>
          </div>
        )}

        {adminItems.length > 0 && (
          <div>
            <h3 className="flex items-center gap-2 font-semibold text-gray-900 mb-3"><PersonIcon /> Facility Administration</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {adminItems.map((d) => (
                <SeatStepper key={d.key} icon={iconFor[d.key]} label={d.label} description={d.hours}
                  value={seats[d.key]} onChange={(v) => setSeats({ ...seats, [d.key]: v })} />
              ))}
            </div>
          </div>
        )}

        {availableItems.length === 0 && (
          <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4 text-sm text-yellow-900">
            No training plans are currently available for this facility type. Please contact support.
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-md bg-green-50 border border-green-100 p-3 flex items-center gap-2">
            <PersonIcon />
            <div>
              <p className="text-xs text-gray-500">Total seats selected</p>
              <p className="font-semibold text-gray-900">{totalSeats} {totalSeats === 1 ? 'seat' : 'seats'}</p>
            </div>
          </div>
          <div className="rounded-md bg-green-50 border border-green-100 p-3 flex items-center gap-2">
            <ShieldIcon className="h-5 w-5 text-green-600" />
            <div>
              <p className="text-xs text-gray-500">Volume discount</p>
              <p className="font-semibold text-gray-900">{currentTier ? (currentTier === '5-9' ? '5%' : '10%') + ' applied' : 'None yet'}</p>
            </div>
          </div>
        </div>

        {showContactOption && (
          <div className="rounded-md bg-indigo-50 border border-indigo-100 p-3 text-sm text-indigo-900">
            Buying 20 or more seats? You can continue with self-serve checkout, or{' '}
            <button type="button" onClick={() => setContactSalesOpen(true)} className="font-semibold underline hover:text-indigo-700 cursor-pointer">contact sales</button>{' '}
            for custom pricing.
          </div>
        )}

        {totalSeats > 0 && (
          <div className="space-y-3 pt-2">
              <p className="text-sm font-semibold text-gray-800">Choose your payment option</p>
              {planAvailability.subscription && (
                <label className={`block cursor-pointer rounded-lg border-2 p-4 transition-colors ${plan === 'subscription' ? 'border-indigo-600 bg-indigo-50/40' : 'border-gray-200 hover:border-indigo-300'}`}>
                  <div className="flex items-start gap-3">
                    <input type="radio" name="plan" checked={plan === 'subscription'} onChange={() => setPlan('subscription')} className="mt-1 h-4 w-4 text-indigo-600" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900">Team Subscription – Full Access</h3>
                        <span className="inline-flex items-center rounded-md bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">Best Value</span>
                      </div>
                      <p className="mt-1 text-sm text-gray-600">Keep your team compliant year-round with automatic renewal and uninterrupted training access.</p>
                    </div>
                  </div>
                </label>
              )}
              {planAvailability.one_time && (
                <label className={`block cursor-pointer rounded-lg border-2 p-4 transition-colors ${plan === 'one_time' ? 'border-indigo-600 bg-indigo-50/40' : 'border-gray-200 hover:border-indigo-300'}`}>
                  <div className="flex items-start gap-3">
                    <input type="radio" name="plan" checked={plan === 'one_time'} onChange={() => setPlan('one_time')} className="mt-1 h-4 w-4 text-indigo-600" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">One-Time Team Bundle</h3>
                      <p className="mt-1 text-sm text-gray-600">Pay once for access to complete this training within the current cycle.</p>
                    </div>
                  </div>
                </label>
              )}
              {!planAvailability.subscription && !planAvailability.one_time && (
                <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-900">
                  No common payment option is available for your current seat mix. Adjust your selection to continue.
                </div>
              )}
              <div className="rounded-md bg-gray-50 border border-gray-200 p-4 space-y-2">
                <div className="flex justify-between text-sm text-gray-700">
                  <span>Subtotal</span><span>{formatPrice(orderSubtotal)}</span>
                </div>
                {orderDiscount > 0 && (
                  <div className="flex justify-between text-sm font-medium text-green-700">
                    <span>Volume discount</span><span>−{formatPrice(orderDiscount)}</span>
                  </div>
                )}
                <div className="pt-2 border-t border-gray-200 flex justify-between items-center">
                  <span className="font-semibold text-gray-900">Total</span>
                  <span className="text-xl font-bold text-indigo-600">{formatPrice(orderTotal)}</span>
                </div>
              </div>
          </div>
        )}

        <div className="flex justify-between gap-3">
          <button type="button" onClick={() => router.visit('/signup/group/facility')} className={secondaryBtnClass}>Back</button>
          <button type="button" onClick={handleContinue} disabled={!canAdvance || submitting} className={primaryBtnClass}>
            {submitting ? 'Preparing checkout…' : 'Continue to payment'}
          </button>
        </div>
      </div>

      <ContactSalesModal
        open={contactSalesOpen}
        onClose={() => setContactSalesOpen(false)}
        buyer={buyer} facility={facility} totalSeats={totalSeats} seatsBreakdown={seats}
      />
    </SignupLayout>
  );
}
